import Phaser from 'phaser';
import type { BattlePoint } from '../types/battle';
import { Battlefield } from './Battlefield';
import { Formation } from './Formation';

export class TacticalAI {
  private accumulator = 0;
  private committedReserve = false;

  constructor(private readonly side: 'attacker' | 'defender', private readonly battlefield: Battlefield) {}

  update(delta: number, formations: Formation[], enemies: Formation[]) {
    this.accumulator += delta;
    if (this.accumulator < 850) return;
    this.accumulator = 0;
    const active = formations.filter((formation) => formation.alive && !formation.withdrawn);
    const enemyActive = enemies.filter((formation) => formation.alive && !formation.withdrawn);
    if (!active.length || !enemyActive.length) return;

    const activeRatio = active.reduce((sum, formation) => sum + formation.combatEffectiveness(), 0)
      / Math.max(1, enemyActive.reduce((sum, formation) => sum + formation.combatEffectiveness(), 0));
    const averageMorale = active.reduce((sum, formation) => sum + formation.morale, 0) / active.length;
    const frontline = active.filter((formation) => !formation.reserve);
    const reserve = active.filter((formation) => formation.reserve);

    if (!this.committedReserve && reserve.length && (activeRatio < 0.92 || frontline.some((formation) => formation.strengthRatio() < 0.55))) {
      this.committedReserve = true;
      for (const formation of reserve) {
        formation.reserve = false;
        formation.setOrder(formation.profile.role === 'cavalry' || formation.profile.role === 'elephant' ? 'charge' : 'attack');
      }
    }
    if (averageMorale < 24 && activeRatio < 0.7) {
      for (const formation of active) formation.setOrder('withdraw');
      return;
    }

    for (const formation of active) {
      if (formation.routed || formation.withdrawn) continue;
      const target = this.chooseTarget(formation, enemyActive);
      if (!target) continue;
      const range = Phaser.Math.Distance.Between(formation.x, formation.y, target.x, target.y);
      const role = formation.profile.role;
      if (formation.reserve && !this.committedReserve) {
        formation.setOrder('hold');
        continue;
      }
      if (role === 'artillery') {
        if (formation.ammo <= 0) formation.setOrder('hold');
        else if (range > formation.profile.maxRange * 0.94) formation.setOrder('move', this.supportPoint(target, formation, 0.78));
        else formation.setOrder('attack', undefined, target);
        continue;
      }
      if (role === 'ranged' || role === 'gunpowder') {
        const cavalryThreat = enemyActive.find((enemy) => enemy.profile.role === 'cavalry' && Phaser.Math.Distance.Between(formation.x, formation.y, enemy.x, enemy.y) < 210);
        if (cavalryThreat) formation.setOrder('move', this.retreatBehindLine(formation, active));
        else formation.setOrder('skirmish', undefined, target);
        continue;
      }
      if (role === 'cavalry') {
        const vulnerable = enemyActive.filter((enemy) => ['ranged', 'gunpowder', 'artillery'].includes(enemy.profile.role))
          .sort((a, b) => Phaser.Math.Distance.Between(formation.x, formation.y, a.x, a.y) - Phaser.Math.Distance.Between(formation.x, formation.y, b.x, b.y))[0];
        const cavalryTarget = vulnerable ?? target;
        const flank = this.flankPoint(cavalryTarget, formation.side);
        if (range > 180 && Phaser.Math.Distance.Between(formation.x, formation.y, flank.x, flank.y) > 70) formation.setOrder('move', flank);
        else formation.setOrder('charge', undefined, cavalryTarget);
        continue;
      }
      if (role === 'spear') {
        const mounted = enemyActive.find((enemy) => ['cavalry', 'elephant'].includes(enemy.profile.role));
        if (mounted && Phaser.Math.Distance.Between(formation.x, formation.y, mounted.x, mounted.y) < 260) formation.setOrder('attack', undefined, mounted);
        else if (this.side === 'defender' && range > 210) formation.setOrder('hold');
        else formation.setOrder('attack', undefined, target);
        continue;
      }
      if (role === 'elephant') {
        const safer = enemyActive.find((enemy) => enemy.profile.role !== 'spear' && enemy.profile.role !== 'artillery');
        formation.setOrder('charge', undefined, safer ?? target);
        continue;
      }
      if (this.side === 'defender' && !this.centerThreatened(enemyActive) && range > 190) formation.setOrder('hold');
      else formation.setOrder(activeRatio > 1.18 ? 'charge' : 'attack', undefined, target);
    }
  }

  private chooseTarget(formation: Formation, enemies: Formation[]) {
    const objective = this.battlefield.objectives[0];
    return [...enemies].sort((left, right) => {
      const leftDistance = Phaser.Math.Distance.Between(formation.x, formation.y, left.x, left.y);
      const rightDistance = Phaser.Math.Distance.Between(formation.x, formation.y, right.x, right.y);
      const leftThreat = left.profile.role === 'artillery' ? -80 : ['ranged', 'gunpowder'].includes(left.profile.role) ? -35 : 0;
      const rightThreat = right.profile.role === 'artillery' ? -80 : ['ranged', 'gunpowder'].includes(right.profile.role) ? -35 : 0;
      const leftObjective = objective ? Phaser.Math.Distance.Between(left.x, left.y, objective.x, objective.y) * 0.08 : 0;
      const rightObjective = objective ? Phaser.Math.Distance.Between(right.x, right.y, objective.x, objective.y) * 0.08 : 0;
      return leftDistance + leftThreat + leftObjective - (rightDistance + rightThreat + rightObjective);
    })[0];
  }

  private flankPoint(target: Formation, side: 'attacker' | 'defender'): BattlePoint {
    const direction = side === 'attacker' ? 1 : -1;
    const offsetY = target.y < this.battlefield.bounds.centerY ? 125 : -125;
    return this.battlefield.clampToBattle({ x: target.x - direction * 95, y: target.y + offsetY });
  }

  private supportPoint(target: Formation, formation: Formation, factor: number): BattlePoint {
    const dx = formation.x - target.x;
    const dy = formation.y - target.y;
    const distance = Math.max(1, Math.hypot(dx, dy));
    return this.battlefield.clampToBattle({ x: target.x + dx / distance * formation.profile.maxRange * factor, y: target.y + dy / distance * formation.profile.maxRange * factor });
  }

  private retreatBehindLine(formation: Formation, allies: Formation[]): BattlePoint {
    const melee = allies.filter((ally) => ['melee', 'spear', 'elephant'].includes(ally.profile.role) && ally.alive);
    const anchor = melee.sort((a, b) => Phaser.Math.Distance.Between(formation.x, formation.y, a.x, a.y) - Phaser.Math.Distance.Between(formation.x, formation.y, b.x, b.y))[0];
    if (!anchor) return this.battlefield.clampToBattle({ x: formation.x + (formation.side === 'attacker' ? -110 : 110), y: formation.y });
    return this.battlefield.clampToBattle({ x: anchor.x + (formation.side === 'attacker' ? -90 : 90), y: anchor.y });
  }

  private centerThreatened(enemies: Formation[]) {
    const objective = this.battlefield.objectives[0];
    return Boolean(objective && enemies.some((enemy) => Phaser.Math.Distance.Between(enemy.x, enemy.y, objective.x, objective.y) < objective.radius * 2.3));
  }
}
